import { SidePanelStatus } from "@nova/component-side-panel-tab-container"
import { ImageProcessingState, JobStatusType, JobTypes } from "@nova/constants"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { getJobStatus } from "../module"
import { getSummaryFromUploads } from "../upload-flow/module"
import { useJobProgress } from "../useJobProgress"
import { selectOrthoFlightById } from "features/entity-caching/flights/selectors"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import {
    staleLiveLayerTimeout,
    StatusAndProgressData,
} from "../generics/GenericLayerListItemProgress"

export function useOrthoStatusAndProgress(id: string): StatusAndProgressData {
    const entity = useAppSelector(selectOrthoFlightById(id))
    const progress = useJobProgress({
        job: entity.job,
        type: JobTypes.Ortho,
    })
    const uploads = useAppSelector(selectOrthoImagesAndUploadsByFlightId(id))
    const summary = getSummaryFromUploads(uploads)
    const completedUploadCount = summary.stateCount?.[ImageProcessingState.IngestDone]
    const totalUploadCount = summary.uploadCount
    const jobStatus = getJobStatus(summary, entity.job?.status)
    let status = !totalUploadCount ? SidePanelStatus.empty : jobStatus

    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(id))
    const createdAt = useMemo(
        () =>
            !!entity.job ? dateFromNumber(entity.job?.createdAt).toISOString() : uploadTimestamp,
        [entity.job, uploadTimestamp]
    )

    if (entity.job?.status === JobStatusType.LIVE) {
        if (entity.job.extraFeatures?.includes("liveScan")) {
            return {
                status: SidePanelStatus.live,
                imageCount: uploads?.length ?? 0,
                createdBy: entity.createdBy ? { userId: entity.createdBy.userId } : undefined,
                createdAtDate: createdAt ? new Date(createdAt) : undefined,
            }
        }
        return {
            status: SidePanelStatus.loading,
            progress,
            createdAtDate: createdAt ? new Date(createdAt) : undefined,
            isPreview: !!entity.cogOrtho?.fileId,
        }
    }

    let createdAtDate: Date | undefined
    if (createdAt) {
        createdAtDate = new Date(createdAt)
        if (
            status === SidePanelStatus.loading &&
            new Date().getTime() - createdAtDate.getTime() >= staleLiveLayerTimeout
        ) {
            status = SidePanelStatus.failed
        }
    }
    switch (status) {
        case SidePanelStatus.loading: {
            return {
                status,
                progress,
                createdAtDate,
            }
        }
        case SidePanelStatus.uploading: {
            return {
                status,
                totalUploadCount,
                completedUploadCount,
                createdAtDate,
            }
        }
    }
    return {
        status,
    }
}
